import { TOAST_TYPE } from '@client/common/components/Toast';
import { requestApiSaga } from '@client/common/redux/api/sagas';
import { reportError } from '@client/common/redux/errors/actions';
import { parseError } from '@client/common/utils/api/error';
import { showToast } from '@client/common/utils/showToast';
import { config } from '@client/config';
import { Forms } from '@client/content/api/Forms';
import {
  all,
  AllEffect,
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  takeLatest,
} from 'redux-saga/effects';
import { APP_ENV } from 'b2b-server/core/utils/env';
import {
  SwissEnterpriseRegisterApiError,
  validateUID as validateUIDApi,
} from '../services/swissEnterpriseRegisterApi';
import { UIDApiResponse } from '../services/swissEnterpriseRegisterApi.types';
import { B2BRegistrationStep, PROD_FORM_ID, STAGE_FORM_ID } from '../utils/constants';
import { formatFormDataAsString } from '../utils/formDataFormatter';
import { clearLocalStorageCache } from '../utils/localStorage';
import {
  setCurrentStep,
  submitForm,
  submitFormFailure,
  submitFormRequest,
  submitFormSuccess,
  validateUID,
  validateUIDFailure,
  validateUIDRequest,
  validateUIDSuccess,
} from './redux';

function* validateUIDSaga(
  action: ReturnType<typeof validateUID>,
): Generator<CallEffect | PutEffect, void, UIDApiResponse> {
  const { uid, onSuccess, onFailure } = action.payload;

  yield put(validateUIDRequest(uid));

  try {
    const response: UIDApiResponse = yield call(validateUIDApi, uid);

    yield put(validateUIDSuccess(response));

    if (onSuccess) {
      onSuccess(response);
    }

    yield put(setCurrentStep(B2BRegistrationStep.CompanyDetails));
  } catch (error) {
    if (error instanceof SwissEnterpriseRegisterApiError) {
      const errorPayload = { uid, error: { message: error.code, statusCode: 500 } };
      yield put(validateUIDFailure(errorPayload));

      if (onFailure) {
        onFailure(errorPayload.error);
      }

      if (error.code !== 'NOT_FOUND') {
        yield put(setCurrentStep(B2BRegistrationStep.CompanyDetails));
      }
    } else {
      const parsedError = parseError(error);
      const errorPayload = { uid, error: { ...parsedError, message: 'NETWORK_ERROR' } };
      yield put(validateUIDFailure(errorPayload));

      if (onFailure) {
        onFailure(errorPayload.error);
      }

      yield put(setCurrentStep(B2BRegistrationStep.CompanyDetails));
      yield put(reportError({ message: action.type, error }));
    }
  }
}

function* submitFormSaga(
  action: ReturnType<typeof submitForm>,
): Generator<CallEffect | PutEffect, void, void> {
  const { data, onSuccess } = action.payload;

  yield put(submitFormRequest(data));

  try {
    const formId = config.env === APP_ENV.production ? PROD_FORM_ID : STAGE_FORM_ID;
    const payload = { body: formatFormDataAsString(data) };

    yield call(requestApiSaga, Forms.submit(formId, payload));

    yield call(clearLocalStorageCache);

    yield put(submitFormSuccess());

    if (onSuccess) {
      onSuccess();
    }

    yield put(setCurrentStep(B2BRegistrationStep.Confirmation));
  } catch (error) {
    const parsedError = parseError(error);
    yield put(submitFormFailure(parsedError));
    yield put(reportError({ message: action.type, error, additionalData: data }));

    showToast(
      TOAST_TYPE.error,
      'Submission Error',
      'An error occurred while submitting your application. Please try again.',
    );
  }
}

export function* b2bRegistrationSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    takeLatest(validateUID.type, validateUIDSaga),
    takeLatest(submitForm.type, submitFormSaga),
  ]);
}
